// App router + global keybindings + modal plumbing.

import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import { Rail } from "./components/Rail";
import { DataProvider, useAppData, useScreenEnabled } from "./store";
import type { Screen } from "./types";
import { InboxScreen } from "./screens/Inbox";
import { UploadScreen } from "./screens/Upload";
import { ArchiveScreen } from "./screens/Archive";
import { LibraryScreen } from "./screens/Library";
import { AntiScreen } from "./screens/Anti";
import { SimulatorScreen } from "./screens/Simulator";
import { RoutesScreen } from "./screens/Routes";
import { RouteDetailScreen } from "./screens/RouteDetail";
import { FieldsScreen } from "./screens/Fields";
import { RulesScreen } from "./screens/Rules";
import { ListsScreen } from "./screens/Lists";
import { TagsScreen } from "./screens/Tags";
import { EnrichmentsScreen } from "./screens/Enrichments";
import { TeamScreen } from "./screens/Team";
import { IntegrationsScreen } from "./screens/Integrations";
import { DetailPanel } from "./screens/Detail";
import { PersonPanel } from "./screens/PersonPanel";
import { DecideModal, type DecideKind } from "./screens/DecideModal";
import { Cmdk } from "./screens/Cmdk";
import { Login } from "./screens/Login";
import { FieldEditModal } from "./screens/FieldEditModal";
import { RuleEditModal } from "./screens/RuleEditModal";
import { KnowledgeScreen } from "./screens/Knowledge";

interface AppState {
  screen: Screen;
  routeId: string | null;
  openId: string | null;
  /** A person dossier layered over the deal slide-over (founder chips). */
  openPersonId: string | null;
  decide: { id: string; kind: DecideKind } | null;
  editField: string | null;
  editRule: string | "new" | null;
  /** When set, the Inbox is filtered to this member's deals (Team → view in inbox). */
  inboxOwnerId: string | null;
}

function screenFromPath(): Screen {
  const path = window.location.pathname.replace(/^\//, "").split("/")[0];
  const valid: Screen[] = [
    "inbox", "upload", "archive", "docs", "anti", "sim", "knowledge",
    "routes", "fields", "rules", "lists", "tags",
    "enrichments", "team", "integrations",
  ];
  return valid.includes(path as Screen) ? (path as Screen) : "inbox";
}

function Shell() {
  const { data, loading, error } = useAppData();
  const screenEnabled = useScreenEnabled();
  const [s, set] = useState<AppState>({
    screen: screenFromPath(), routeId: null, openId: null, openPersonId: null, decide: null,
    editField: null, editRule: null, inboxOwnerId: null,
  });
  const [cmdkOpen, setCmdkOpen] = useState(false);

  useEffect(() => {
    const onPop = () => set((prev) => ({
      ...prev, screen: screenFromPath(), openId: null, openPersonId: null, decide: null,
      editField: null, editRule: null, inboxOwnerId: null,
    }));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdkOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const go = (screen: Screen) => {
    window.history.pushState(null, "", "/" + screen);
    set({
      screen, routeId: null, openId: null, openPersonId: null, decide: null,
      editField: null, editRule: null, inboxOwnerId: null,
    });
  };

  // Team → "view in inbox": jump to the Inbox filtered to one member's deals.
  const goToInboxFor = (memberId: string) => {
    window.history.pushState(null, "", "/inbox");
    set({
      screen: "inbox", routeId: null, openId: null, openPersonId: null, decide: null,
      editField: null, editRule: null, inboxOwnerId: memberId,
    });
  };

  if (loading || !data) {
    return (
      <div style={{
        display: "grid", placeItems: "center",
        height: "100vh", color: "var(--ink-3)", fontSize: 13,
      }}>
        {error ? `Couldn't load: ${error}` : "Loading…"}
      </div>
    );
  }

  // Route editor flow (a subpage of Settings → Routes).
  if (s.screen === "routes" && s.routeId) {
    return (
      <div className="app">
        <Rail current={s.screen} onNav={go} />
        <RouteDetailScreen
          id={s.routeId}
          onBack={() => set({ ...s, routeId: null })}
          onEditField={(id) => set({ ...s, editField: id })}
          onEditRule={(id) => set({ ...s, editRule: id })}
        />
        {s.editField && (
          <FieldEditModal
            id={s.editField}
            onClose={() => set({ ...s, editField: null })}
          />
        )}
        {s.editRule && (
          <RuleEditModal
            id={s.editRule}
            onClose={() => set({ ...s, editRule: null })}
          />
        )}
        {cmdkOpen && (
          <Cmdk
            onClose={() => setCmdkOpen(false)} onNav={go}
            onOpenDeal={(id) => { setCmdkOpen(false); set({ ...s, screen: "inbox", openId: id }); }}
          />
        )}
      </div>
    );
  }

  // A screen disabled by the firm's plan falls back to the inbox.
  const screen: Screen = screenEnabled(s.screen) ? s.screen : "inbox";

  let body: React.ReactNode;
  switch (screen) {
    case "inbox":       body = <InboxScreen onOpen={(id) => set({ ...s, openId: id })} ownerFilterId={s.inboxOwnerId} onClearOwnerFilter={() => set({ ...s, inboxOwnerId: null })} />; break;
    case "upload":      body = <UploadScreen onOpenDeal={(id) => set({ ...s, openId: id })} />; break;
    case "archive":     body = <ArchiveScreen onOpen={(id) => set({ ...s, openId: id })} />; break;
    case "docs":        body = <LibraryScreen onOpenDeal={(id) => set({ ...s, openId: id })} />; break;
    case "anti":        body = <AntiScreen />; break;
    case "sim":         body = <SimulatorScreen />; break;
    case "knowledge":   body = <KnowledgeScreen onOpenDeal={(id) => set({ ...s, openId: id })} />; break;
    case "routes":      body = <RoutesScreen onOpen={(id) => set({ ...s, routeId: id })} />; break;
    case "fields":      body = <FieldsScreen onEdit={(id) => set({ ...s, editField: id })} />; break;
    case "rules":       body = <RulesScreen onEdit={(id) => set({ ...s, editRule: id })} />; break;
    case "lists":       body = <ListsScreen />; break;
    case "tags":        body = <TagsScreen />; break;
    case "enrichments": body = <EnrichmentsScreen />; break;
    case "team":        body = <TeamScreen onJumpToInbox={goToInboxFor} />; break;
    case "integrations": body = <IntegrationsScreen />; break;
    default:            body = <InboxScreen onOpen={(id) => set({ ...s, openId: id })} />;
  }

  return (
    <div className="app">
      <Rail current={screen} onNav={go} />
      {body}
      {s.openPersonId ? (
        <PersonPanel
          id={s.openPersonId}
          onBack={() => set({ ...s, openPersonId: null })}
          onClose={() => set({ ...s, openId: null, openPersonId: null })}
          onOpenDeal={(id) => set({ ...s, openId: id, openPersonId: null })}
        />
      ) : s.openId && (
        <DetailPanel
          id={s.openId}
          onClose={() => set({ ...s, openId: null, openPersonId: null })}
          onDecide={(kind) => set({ ...s, decide: { id: s.openId!, kind } })}
          onOpenDeal={(id) => set({ ...s, openId: id })}
          onOpenPerson={(pid) => set({ ...s, openPersonId: pid })}
        />
      )}
      {s.decide && (
        <DecideModal
          id={s.decide.id} kind={s.decide.kind}
          onClose={() => set({ ...s, decide: null })}
          onSaved={() => set({ ...s, decide: null, openId: null })}
        />
      )}
      {s.editField && (
        <FieldEditModal id={s.editField} onClose={() => set({ ...s, editField: null })} />
      )}
      {s.editRule && (
        <RuleEditModal id={s.editRule} onClose={() => set({ ...s, editRule: null })} />
      )}
      {cmdkOpen && (
        <Cmdk
          onClose={() => setCmdkOpen(false)} onNav={go}
          onOpenDeal={(id) => { setCmdkOpen(false); set({ ...s, screen: "inbox", openId: id }); }}
        />
      )}
    </div>
  );
}

function Gate() {
  const { status } = useAuth();
  if (status === "loading") {
    return (
      <div style={{
        display: "grid", placeItems: "center",
        height: "100vh", color: "var(--ink-3)", fontSize: 13,
      }}>Loading…</div>
    );
  }
  if (status === "signedOut") return <Login />;
  return (
    <DataProvider>
      <Shell />
    </DataProvider>
  );
}

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
